import gameConfigurations from '@configs/gameConfigurations'
import { create } from 'zustand'
import {
    BaseGameType,
    ViewportType,
    DefaultLocalization,
    SlotGameConfigurationState,
    BaseGamesDefaultLocalization,
    GameUIMessageType,
    BackgroundMusicClickedMessage,
} from 'game-ui-types'
import { ComponentConfiguration } from '@configs/types'
import { getParam, getTournamentMode, hasFlag } from '@amusnet/react-ui'
import { gameAnalytics } from '@utils/gameAnalytics'

interface ConfigurationFeatureFlags {
    shouldDisableJackpotHistory: boolean
    shouldUseReactJackpotGame: boolean
    shouldUseReactGambleGame: boolean
}

export interface ConfigurationStoreState extends SlotGameConfigurationState, ConfigurationFeatureFlags {
    hasWidgetInUI: boolean // TODO: remove when promo removed from platform
    hasMysteryWinInUI: boolean // TODO: remove when mystery win removed from pixi
    shouldHideFullScreenButton: boolean
    shouldHideJackpotWinnerNames: boolean
    isExpoTournamentModeHidden: boolean
    hasUltraPlay: boolean
    isUltraPlaySupported: boolean
    hasAccumulatedMaxCap: boolean
    setHasAccumulatedMaxCap: (hasAccumulatedMaxCap: boolean) => void
    setConfiguration: (config: Partial<SlotGameConfigurationState & ConfigurationFeatureFlags>) => void
    setLanguage: (language: string) => void
    getComponentConfig: (component: string) => ComponentConfiguration
    computedReelSizes: () => Record<ViewportType, { width: number; height: number; x: number; y: number }>
    setIsSoundMuted: (isMuted: boolean, isByUser?: boolean) => void
    setIsBackgroundMusicMuted: (isMuted: boolean, isByUser?: boolean) => void
    setIsFullscreen: (isFullscreen: boolean) => void
    setDoubleChanceMultiplier: (doubleChanceMultiplier: number) => void
    isGameLoaded: boolean
    setIsGameLoaded: (isGameLoaded: boolean) => void
    isFeatureRunning: boolean
    setIsFeatureRunning: (isFeatureRunning: boolean) => void
    shouldUseExternalMenu: boolean
    paytableData: Record<string, unknown>
    setPaytableData: (paytableData: Record<string, unknown>) => void
    setPaytableUrl: (paytableUrl: string) => void
    paytableBasePath: string
    paytableLanguages: string[]
    setPaytableBasePath: (paytableBasePath: string) => void
    isPaytableConfirmationAccepted: boolean
    setIsPaytableConfirmationAccepted: (isPaytableConfirmationAccepted: boolean) => void
    playerUid: string
    setPlayerUid: (playerUid: string) => void
    apiBaseUrl: string
    setApiBaseUrl: (apiBaseUrl: string) => void
    portalUid: string
    setPortalUid: (portalUid: string) => void
    portalCode: string
    setPortalCode: (portalCode: string) => void
    promoModes: string[]
    setPromoModes: (promoModes: string[]) => void
}

/**
 * Builds a language-specific paytable URL based on current language and paytable languages for the game
 */
export const resolvePaytableUrl = (language: string): string => {
    const { assetsPath, modeType } = window.gameConfig!
    const promoSuffix = modeType ? `/${modeType}` : ''
    let basePath = '../'

    if (location.hostname.includes('staging')) {
        basePath = '../../'
    }

    if (location.hostname.includes('localhost')) {
        basePath = '../generated/'
    }

    return `${basePath}paytable/${assetsPath}${promoSuffix}/${language}.html?v=${Date.now()}`
}

export const useConfigurationStore = create<ConfigurationStoreState>((set, get) => ({
    paytableData: {},
    setPaytableData: (paytableData) => set({ paytableData }),
    paytableUrl: '',
    setPaytableUrl: (paytableUrl) => set({ paytableUrl }),
    paytableBasePath: '',
    paytableLanguages: [],
    setPaytableBasePath: (paytableBasePath) => set({ paytableBasePath }),
    gameHistoryApi: '',
    gameHistoryUrl: '',
    gin: -1,
    token: '',
    betOptionActiveIndex: -1,
    gameType: BaseGameType.SLOT_CONFIG_A, // Default game type
    currency: '', // Set from the SETUP configuration; empty until then so features
    // (e.g. cash bomb) don't initialise with a placeholder currency.
    currencyHideCents: false,
    language: 'en',
    playerName: '',
    playerUid: '',
    amount: 0,
    betGroupId: '',
    shouldHideTotalFreespinsText: false,
    betOptions: [], // Default bet options
    hasCustomResult: false, // Default custom result
    hasBuyBonus: false,
    hasDoubleChance: false,
    shouldHideHelpSection: false,
    hasUltraPlay: !getParam('extraFeatures')?.includes('hasUltraPlay:false'),
    isUltraPlaySupported: false,
    hasAccumulatedMaxCap: false, // Used in game history
    doubleChanceMultiplier: 1, // Default double chance multiplier
    setDoubleChanceMultiplier: (doubleChanceMultiplier: number) => set({ doubleChanceMultiplier }),
    initialFeatureName: 'NONE', // Default initial feature name
    autoplayOptions: [], // Default autoplay options
    autoplaySettings: [],
    isAutoplayDisabled: false, // Default autoplay disabled
    shouldHideDisabledAutoplayButton: false, // Default hide disabled autoplay button
    centsVisibilityMoneyLimit: 9999, // Default cents visibility money limit
    shouldShowCentsOnMoneyLimit: false, // Default show cents on money limit
    shouldBalanceUpdateImmediately: false, // Default balance update immediately state
    shouldBalanceSubtractOnly: Boolean(getParam('extraFeatures')?.includes('shouldBalanceSubtractOnly:true')), // When active, balance ignores wins/collects and only decreases on bets
    chanceOfMaxWinProbability: '1:25000000', // Default chanceOfMaxWinProbability
    shouldShowSpeedButton: false, // Default speed button visibility
    shouldEnableUltraSpeed: getParam('extraFeatures')?.includes('shouldEnableUltraSpeed:true'), // Default ultra speed state
    shouldShowLeftHandModeButton: false, // Default left hand mode button visibility
    shouldUseExternalMenu: false, // Default external menu button state
    shouldShowPaytableConfirmation: false, // Default should show paytable confirmation state
    shouldDisableTurbospin: false, // Default turbospin state
    shouldDisableSpaceKey: false, // Default space key state
    shouldShowUTCTime: false, // Default UTC time visibility
    shouldHideJackpotWinnerNames: hasFlag('shouldHideJackpotWinnerNames'),
    shouldDisableJackpotHistory: getParam('extraFeatures')?.includes('shouldDisableJackpotHistory:true') ?? false,
    shouldUseReactJackpotGame: false,
    shouldUseReactGambleGame: false,
    sendTotalsInfo: false, // Default send totals info
    minimumSpinTimeDuration: 0, // Default minimum spin time duration - value in seconds
    viewport: ViewportType.MOBILE_PORTRAIT, // Default viewport
    localization: { ...DefaultLocalization, ...BaseGamesDefaultLocalization },
    isSoundMuted: false,
    isBackgroundMusicMuted: false,
    isFullscreen: false,
    isGameLoaded: false, // Default game loaded state
    isFeatureRunning: false, // Default feature running state
    isHistoryMode: false, // Default history mode
    isRemoteGame: false, // Default remote game
    isReplayMode: false, // Default replay mode
    isReplaySupported: true,
    isReplayEnabled: true,
    isPaytableConfirmationAccepted: false, // Default paytable confirmation switch state
    shouldUseDynamicStopButtonSize: false,
    shouldOpenAutoplaySettings: hasFlag('forceMenuAutoplayTab'), // Open autoplay settings on autoplay button click by default
    numReels: 5, // Default value
    buyBonusConfig: {
        maxBet: 800,
        choiceCount: 1,
        layout: {
            headerColors: [],
            bodyColors: [],
            footerColors: [],
            allSymbolsBackgroundColors: [],
        },
        features: [
            {
                name: 'BUY_BONUS',
                label: 'Bonus',
                multiplier: 20,
                labelFontSize: '36px',
                featureGroup: 1,
            },
        ],
        hasGuaranteedWin: false, // Default guaranteed win state
        shouldTheFeatureStayActive: false, // Default feature stay active state
    },
    hasExternalBetAmountPlayerInfo: false, // Default support external bet amount in player info
    hasLastWinPlayerInfoPortrait: true, // Default show last win info in portrait
    shouldUseAmuseBoost: true,
    hasWidgetInUI: getTournamentMode() !== 'expo:hidden',
    isExpoTournamentModeHidden: getTournamentMode() === 'expo:hidden',
    shouldHideFullScreenButton: getParam('extraFeatures')?.includes('shouldHideFullScreenButton:true'),
    hasMysteryWinInUI: getParam('extraFeatures')?.includes('hasMysteryWinInUI:true'), // TODO: remove when mystery win v2 in External UI only
    useMysteryTextVariant: true,
    canvasDimensions: {
        // Default canvas sizes
        [ViewportType.MOBILE_PORTRAIT]: { width: 810, height: 1440 },
        [ViewportType.MOBILE_LANDSCAPE]: { width: 1440, height: 810 },
        [ViewportType.DESKTOP]: { width: 1920, height: 1080 },
    },
    stageDimensions: {
        [ViewportType.MOBILE_PORTRAIT]: { width: 810, height: 1440 },
        [ViewportType.MOBILE_LANDSCAPE]: { width: 1440, height: 810 },
        [ViewportType.DESKTOP]: { width: 1920, height: 1080 },
    },
    reel: {
        // Use computedReelSizes when using the values
        // Width of a single reel
        // Default reel values for slot config A
        [ViewportType.MOBILE_PORTRAIT]: { width: 144, height: 432, x: 0, y: 0, reelSpacing: 0 },
        [ViewportType.MOBILE_LANDSCAPE]: { width: 198, height: 594, x: 0, y: 0, reelSpacing: 0 },
        [ViewportType.DESKTOP]: { width: 264, height: 792, x: 0, y: 0, reelSpacing: 0 },
    },

    skipWinAnimationsCoef: -1,

    // Update store configuration
    setConfiguration: (config) => {
        set((state) => ({
            ...state,
            ...config,
            paytableUrl: state.paytableUrl || config.paytableUrl,
            shouldUseExternalMenu: true,
        }))
    },

    setLanguage: (language: string) => set({ language }),

    setHasAccumulatedMaxCap: (hasAccumulatedMaxCap: boolean) => set({ hasAccumulatedMaxCap }),

    // Retrieve component-specific configuration with fallback
    getComponentConfig: (component) => {
        const { viewport, gameType, isFeatureRunning } = get()

        // Current game type configuration
        const gameConfig = gameConfigurations[gameType]?.viewports?.[viewport]?.[component]

        // Fallback to base configuration if current is missing
        const baseConfig = gameConfigurations[BaseGameType.SLOT_CONFIG_A].viewports?.[viewport]?.[component]

        // While a feature runs, a `<component>Feature` entry takes over for as long as the current
        // game type and viewport declare one
        const featureConfig = isFeatureRunning
            ? gameConfigurations[gameType]?.viewports?.[viewport]?.[`${component}Feature`]
            : undefined

        return {
            ...baseConfig, // Base properties
            ...gameConfig, // Override with current type properties
            ...featureConfig, // Override with the feature layout
        }
    },

    computedReelSizes: () => {
        // Checks the dimensions of the stage and the canvas and if they're not equal scales the value accordingly
        const { reel, stageDimensions, canvasDimensions } = get()

        return Object.values(ViewportType).reduce(
            (acc, v) => {
                acc[v] = { ...reel[v] }

                if (stageDimensions[v].width !== canvasDimensions[v].width) {
                    acc[v].width = (reel[v].width * stageDimensions[v].width) / canvasDimensions[v].width
                    acc[v].x = (reel[v].x * stageDimensions[v].width) / canvasDimensions[v].width
                }

                if (stageDimensions[v].height !== canvasDimensions[v].height) {
                    acc[v].height = (reel[v].height * stageDimensions[v].height) / canvasDimensions[v].height
                    acc[v].y = (reel[v].y * stageDimensions[v].height) / canvasDimensions[v].height
                }

                return acc
            },
            {} as Record<ViewportType, { width: number; height: number; x: number; y: number }>
        )
    },
    setIsSoundMuted: (isMuted: boolean, isByUser = true) => {
        set({ isSoundMuted: isMuted })

        if (!isByUser) {
            return
        }

        gameAnalytics.soundToggled(isMuted)

        // we moved the postMessage logic into zustand because we need to fire it from 2 places
        const soundClickedMessage = { type: GameUIMessageType.SOUND_CLICKED, payload: { isMuted } }

        window.uiBus.postMessage(soundClickedMessage)
    },
    setIsBackgroundMusicMuted: (isMuted, isByUser = true) => {
        set({ isBackgroundMusicMuted: isMuted })

        // The engine echoes its own toggles back through MUSIC_BUTTON_TOGGLED; only a real tap counts.
        if (isByUser) {
            gameAnalytics.backgroundMusicToggled(isMuted)
        }

        const { shouldUseExternalMenu } = get()

        // TODO: Delete the if check when new menu is tested
        if (shouldUseExternalMenu) {
            const backgroundMusicMutedMessage: BackgroundMusicClickedMessage = {
                type: GameUIMessageType.BACKGROUND_MUSIC_CLICKED,
            }

            window.uiBus.postMessage(backgroundMusicMutedMessage)
        }
    },
    setIsFullscreen: (isFullscreen) => set({ isFullscreen }),
    setIsGameLoaded: (isGameLoaded) => set({ isGameLoaded }),

    setIsFeatureRunning: (isFeatureRunning) => set({ isFeatureRunning }),
    setIsPaytableConfirmationAccepted: (isPaytableConfirmationAccepted) => set({ isPaytableConfirmationAccepted }),
    setPlayerUid: (playerUid: string) => set({ playerUid }),
    apiBaseUrl: '',
    setApiBaseUrl: (apiBaseUrl: string) => set({ apiBaseUrl }),
    portalUid: '',
    setPortalUid: (portalUid: string) => set({ portalUid }),
    portalCode: '',
    setPortalCode: (portalCode: string) => set({ portalCode }),
    promoModes: [],
    setPromoModes: (promoModes: string[]) => set({ promoModes }),
}))

// Exposed for e2e tests, which can no longer read it from bottle's GameSettings
window.gameUI = {
    get language() {
        return useConfigurationStore.getState().language
    },
}
